import { useState, ReactNode, ChangeEvent, KeyboardEvent, FormEvent } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Stack,
    Tabs,
    Tab,
    Divider,
    styled,
} from '@mui/material';
import { Login as LoginIcon, Key as KeyIcon } from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import { signInMethod } from '@/utils/signInMethod';
import { useGetSettingsQuery, useAuthenticateMutation } from '@/store/apiSlice.ts';

interface TabPanelProps {
    children?: ReactNode;
    index: number;
    value: number;
}

const CodeTextField = styled(TextField)({
    width: 60,
    '& input': {
        textAlign: 'center',
        fontSize: '18px',
        fontWeight: 'bold',
        padding: '20px 6px',
        '&[type=number]': {
            MozAppearance: 'textfield',
        },
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
    },
});

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`login-tabpanel-${index}`}
            aria-labelledby={`login-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

const LoginPage = () => {
    const { LL } = useI18nContext();
    const [tabValue, setTabValue] = useState(0);
    const [code, setCode] = useState(['', '', '', '']);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { data: settings } = useGetSettingsQuery();
    const [authenticate] = useAuthenticateMutation();

    if (!settings) {
        return null;
    }

    const handleTabChange = (_event: unknown, newValue: number) => {
        setTabValue(newValue);
        setError('');
    };

    const handleCodeChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.toUpperCase();
        if (value.length > 1) return;

        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        if (value && index < 3) {
            const next = document.getElementById(`code-${index + 1}`);
            if (next) (next as HTMLInputElement).focus();
        }
    };

    const handleCodeKeyDown = (index: number) => (event: KeyboardEvent) => {
        if (event.key === 'Backspace' && !code[index] && index > 0) {
            const prev = document.getElementById(`code-${index - 1}`);
            if (prev) (prev as HTMLInputElement).focus();
        }
    };

    const handleCodeSubmit = async (e: FormEvent) => {
        e.preventDefault();

        const full = code.join('');
        if (full.length !== 4) {
            setError(LL.login.enterFourDigits());
            return;
        }
        setLoading(true);
        setError('');
        try {
            const result = await authenticate({ code: full }).unwrap();
            if (result.success) {
                signInMethod.rememberCode();
                signInMethod.clearLoggedOut();
                window.location.href = '/';
            } else if (result.code) {
                setError(errorCodeToMessage(result.code, result));
            } else {
                setError(LL.errors.unknownError());
            }
        } catch (err: any) {
            // The server answers with a code like `auth.invalid_code` in `message`
            const serverCode = err.data?.code ?? err.data?.message;
            if (serverCode) {
                setError(errorCodeToMessage(serverCode, err.data));
            } else {
                setError(LL.login.loginFailed({ error: err.message || LL.errors.unknownError() }));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOidcLogin = () => {
        signInMethod.rememberChurchTools();
        signInMethod.clearLoggedOut();
        const redirect = window.location.pathname + window.location.search;
        window.location.href = '/oidc?redirect=' + encodeURIComponent(redirect);
    };

    const { authCodeEnabled, oidcEnabled } = settings.auth;

    // Straight to ChurchTools when it is the only way in, or how this browser signs in — but not
    // with a sign-in error on screen or right after a logout.
    const failed = new URLSearchParams(window.location.search).has('error') || window.location.pathname === '/unauthorized';
    if (oidcEnabled && !failed && (!authCodeEnabled || signInMethod.goStraightToChurchTools())) {
        handleOidcLogin();
    }

    const errorCodeToMessage = (code: string, props?: { details?: string }) => {
        switch (code) {
            case 'oidc.invalid_state':
                return LL.oidcAuth.invalidState();
            case 'oidc.token_exchange_failed':
                return LL.oidcAuth.tokenExchangeFailed();
            case 'oidc.userinfo_failed':
                return LL.oidcAuth.userinfoFailed();
            case 'oidc.access_denied':
                return LL.oidcAuth.accessDenied({
                    group: 'group',
                    groups: 'groups',
                    sub: 'sub',
                    ...props,
                });
            case 'oidc.create_or_update_failed':
                return LL.oidcAuth.failedCreateOrUpdateUser();
            case 'oidc.load_user_session_failed':
                return LL.oidcAuth.failedLoadUserSession();
            case 'oidc.authentication_failed':
                return LL.oidcAuth.authenticationFailed({ message: props?.details || '' });
            case 'auth.not_authenticated':
                return LL.errors.unauthorized();
            case 'auth.invalid_code':
                return LL.login.invalidCode();
            case 'auth.invalid_code_length':
                return LL.login.enterFourDigits();
            case 'auth.too_many_attempts':
                return LL.login.tooManyAttempts();
            case 'auth.code_login_disabled':
                return LL.login.codeLoginDisabled();
            default:
                return LL.errors.unknownError();
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                p: 2,
            }}
        >
            <Card sx={{ maxWidth: 400, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h4" component="h1" gutterBottom>
                            {LL.login.title()}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            {LL.login.subtitle()}
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {authCodeEnabled && oidcEnabled && (
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                        >
                            <Tab
                                label={LL.login.codeTab()}
                                icon={<KeyIcon />}
                                iconPosition="start"
                            />
                            <Tab
                                label={LL.login.ssoTab()}
                                icon={<LoginIcon />}
                                iconPosition="start"
                            />
                        </Tabs>
                    )}

                    {authCodeEnabled && (
                        <TabPanel value={tabValue} index={0}>
                            <form onSubmit={handleCodeSubmit}>
                                <Stack spacing={3}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {LL.login.codePrompt()}
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {code.map((digit, index) => (
                                            <CodeTextField
                                                key={index}
                                                id={`code-${index}`}
                                                value={digit}
                                                type={index < 2 ? 'text' : 'number'}
                                                onChange={handleCodeChange(index)}
                                                onKeyDown={handleCodeKeyDown(index)}
                                                slotProps={{
                                                    htmlInput: {
                                                        maxLength: 1,
                                                    },
                                                }}
                                                autoFocus={index === 0}
                                                disabled={loading}
                                            />
                                        ))}
                                    </Box>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        disabled={loading || code.some((d) => !d)}
                                        startIcon={
                                            loading ? <CircularProgress size={20} /> : <KeyIcon />
                                        }
                                    >
                                        {loading ? LL.login.signingIn() : LL.login.signIn()}
                                    </Button>
                                </Stack>
                            </form>
                        </TabPanel>
                    )}

                    {oidcEnabled && (
                        <TabPanel value={tabValue} index={authCodeEnabled ? 1 : 0}>
                            <Stack spacing={3}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        textAlign: 'center',
                                    }}
                                >
                                    {LL.login.ssoPrompt()}
                                </Typography>

                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    onClick={handleOidcLogin}
                                    startIcon={<LoginIcon />}
                                    sx={{
                                        background:
                                            'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                                        '&:hover': {
                                            background:
                                                'linear-gradient(45deg, #5568d3 30%, #61408a 90%)',
                                        },
                                    }}
                                >
                                    {LL.login.signInWithSSO()}
                                </Button>

                                <Divider>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {LL.login.poweredBy()}
                                    </Typography>
                                </Divider>
                            </Stack>
                        </TabPanel>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default LoginPage;
